import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authAPI, validUser } from '../services/api';
import type { User } from '../types';

interface AuthContextType {
  user:         User | null;
  loading:      boolean;
  error:        string | null;
  retry:        () => void;
  isAdmin:      boolean;
  isSupervisor: boolean;
  isDEO:        boolean;
  // True if user is admin OR supervisor (full feature access except where explicitly restricted)
  canManageAll: boolean;
  login:        (username: string, password: string) => Promise<User>;
  logout:       () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser]       = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  const retry = useCallback(() => setGeneration(value => value + 1), []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const saved = localStorage.getItem('user');
    let active = true;
    setError(null);
    if (token) {
      setLoading(true);
      try { const cached: unknown = JSON.parse(saved || 'null'); if (validUser(cached)) setUser(cached); } catch {}
      authAPI.getMe()
        .then(res => { if (active) { setUser(res.data.data); localStorage.setItem('user', JSON.stringify(res.data.data)); } })
        .catch(failure => {
          if (!active) return;
          if (failure.response?.status === 401) {
            localStorage.removeItem('token'); localStorage.removeItem('user'); setUser(null);
          } else setError('Server busy or unreachable. Your session is retained. Please retry.');
        })
        .finally(() => { if (active) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { active = false; };
  }, [generation]);

  const login = useCallback(async (username: string, password: string): Promise<User> => {
    const res = await authAPI.login({ username, password });
    const { token, user: userData } = res.data.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const isAdmin      = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const isDEO        = user?.role === 'deo';
  const canManageAll = isAdmin || isSupervisor;

  return (
    <AuthContext.Provider value={{ user, loading, error, retry, isAdmin, isSupervisor, isDEO, canManageAll, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
